import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { 
  MessageSquare, 
  Search,
  Filter,
  RefreshCw,
  Eye,
  Smartphone,
  MapPin,
  Calendar
} from 'lucide-react'
import { format } from 'date-fns'
import { useMoMessages, useMoMessageStats, type MoMessage } from '@/hooks/useMoMessages'

export default function MoMessages() {
  const [page, setPage] = useState(1)
  const [searchFilters, setSearchFilters] = useState({
    mobile: '',
    status: 'ALL',
    operator: 'ALL',
    pushStatus: 'ALL',
    content: '',
    startDate: '',
    endDate: '',
  })
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<MoMessage | null>(null)

  // 使用API获取数据
  const { data: messagesData, isLoading, refetch } = useMoMessages({
    page,
    size: 10,
    mobile: searchFilters.mobile || undefined,
    status: searchFilters.status !== 'ALL' ? searchFilters.status : undefined,
    operator: searchFilters.operator !== 'ALL' ? searchFilters.operator : undefined,
    pushStatus: searchFilters.pushStatus !== 'ALL' ? searchFilters.pushStatus : undefined,
    content: searchFilters.content || undefined,
    startDate: searchFilters.startDate || undefined,
    endDate: searchFilters.endDate || undefined,
  })

  // 获取统计信息
  const { data: statsData } = useMoMessageStats()

  // 处理搜索
  const handleSearch = () => {
    setPage(1) // 重置到第一页
  }

  // 处理重置
  const handleReset = () => {
    setSearchFilters({
      mobile: '',
      status: 'ALL',
      operator: 'ALL',
      pushStatus: 'ALL',
      content: '',
      startDate: '',
      endDate: '',
    })
    setPage(1)
  }

  // 处理刷新
  const handleRefresh = () => {
    refetch()
  }

  // 查看详情
  const handleViewDetail = (message: MoMessage) => {
    setSelectedMessage(message)
    setIsDetailDialogOpen(true)
  }

  const messageColumns: ColumnDef<MoMessage>[] = [
    {
      accessorKey: 'messageId',
      header: '消息ID',
      cell: ({ row }) => {
        return <span className="font-mono text-sm">{row.getValue('messageId')}</span>
      },
    },
    {
      accessorKey: 'mobile',
      header: '手机号码',
      cell: ({ row }) => {
        const mobile = row.getValue('mobile') as string
        return (
          <div className="flex items-center space-x-2">
            <Smartphone className="h-4 w-4 text-gray-500" />
            <span>{mobile}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'content',
      header: '消息内容',
      cell: ({ row }) => {
        const content = row.getValue('content') as string
        return (
          <span className="max-w-[200px] truncate" title={content}>
            {content}
          </span>
        )
      },
    },
    {
      accessorKey: 'operator',
      header: '运营商',
      cell: ({ row }) => {
        const operator = row.getValue('operator') as string
        const operatorMap = {
          MOBILE: { label: '移动', variant: 'default' as const },
          UNICOM: { label: '联通', variant: 'secondary' as const },
          TELECOM: { label: '电信', variant: 'outline' as const },
        }
        const operatorInfo = operatorMap[operator as keyof typeof operatorMap] || { label: operator, variant: 'secondary' as const }
        return <Badge variant={operatorInfo.variant}>{operatorInfo.label}</Badge>
      },
    },
    {
      accessorKey: 'location',
      header: '位置',
      cell: ({ row }) => {
        const province = row.getValue('province') as string
        const city = row.getValue('city') as string
        return (
          <div className="flex items-center space-x-1">
            <MapPin className="h-3 w-3 text-gray-500" />
            <span className="text-sm">{province} {city}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: '状态',
      cell: ({ row }) => {
        const status = row.getValue('status') as string
        const statusMap = {
          RECEIVED: { label: '已接收', variant: 'default' as const },
          PROCESSED: { label: '已处理', variant: 'success' as const },
          FAILED: { label: '处理失败', variant: 'destructive' as const },
        }
        const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, variant: 'secondary' as const }
        return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      },
    },
    {
      accessorKey: 'pushStatus',
      header: '推送状态',
      cell: ({ row }) => {
        const pushStatus = row.getValue('pushStatus') as string
        const statusMap = {
          PENDING: { label: '待推送', variant: 'secondary' as const },
          SUCCESS: { label: '推送成功', variant: 'success' as const },
          FAILED: { label: '推送失败', variant: 'destructive' as const },
          DISABLED: { label: '推送禁用', variant: 'outline' as const },
        }
        const statusInfo = statusMap[pushStatus as keyof typeof statusMap] || { label: pushStatus, variant: 'secondary' as const }
        return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      },
    },
    {
      accessorKey: 'createdAt',
      header: '接收时间',
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-1">
            <Calendar className="h-3 w-3 text-gray-500" />
            <span className="text-sm">{format(new Date(row.getValue('createdAt')), 'MM-dd HH:mm:ss')}</span>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        const message = row.original
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewDetail(message)}
          >
            <Eye className="h-4 w-4 mr-1" />
            详情
          </Button>
        )
      },
    },
  ]

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">上行消息</h2>
          {statsData && (
            <p className="text-sm text-muted-foreground mt-1">
              共 {statsData.totalCount} 条消息 • 
              已接收: {statsData.receivedCount} • 
              已处理: {statsData.processedCount} • 
              处理失败: {statsData.failedCount}
            </p>
          )}
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            刷新
          </Button>
        </div>
      </div>

      {/* 搜索筛选区域 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            查询条件
          </CardTitle>
          <CardDescription>设置查询条件来筛选上行消息</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>手机号码</Label>
              <Input
                placeholder="请输入手机号码"
                value={searchFilters.mobile}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, mobile: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>消息内容</Label>
              <Input
                placeholder="请输入消息内容关键词"
                value={searchFilters.content}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>状态</Label>
              <Select value={searchFilters.status} onValueChange={(value) => setSearchFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="选择状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">全部状态</SelectItem>
                  <SelectItem value="RECEIVED">已接收</SelectItem>
                  <SelectItem value="PROCESSED">已处理</SelectItem>
                  <SelectItem value="FAILED">处理失败</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>运营商</Label>
              <Select value={searchFilters.operator} onValueChange={(value) => setSearchFilters(prev => ({ ...prev, operator: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="选择运营商" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">全部运营商</SelectItem>
                  <SelectItem value="MOBILE">移动</SelectItem>
                  <SelectItem value="UNICOM">联通</SelectItem>
                  <SelectItem value="TELECOM">电信</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>推送状态</Label>
              <Select value={searchFilters.pushStatus} onValueChange={(value) => setSearchFilters(prev => ({ ...prev, pushStatus: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="选择推送状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">全部推送状态</SelectItem>
                  <SelectItem value="PENDING">待推送</SelectItem>
                  <SelectItem value="SUCCESS">推送成功</SelectItem>
                  <SelectItem value="FAILED">推送失败</SelectItem>
                  <SelectItem value="DISABLED">推送禁用</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>开始日期</Label>
              <Input
                type="date"
                value={searchFilters.startDate}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>结束日期</Label>
              <Input
                type="date"
                value={searchFilters.endDate}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={handleReset}>
              重置
            </Button>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              查询
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageSquare className="h-5 w-5 mr-2" />
            上行消息列表
          </CardTitle>
          <CardDescription>
            查看所有接收到的用户回复短信
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">加载中...</div>
            </div>
          ) : (
            <DataTable
              columns={messageColumns}
              data={messagesData?.records || []}
              pagination={{
                page: page,
                totalPages: messagesData?.pages || 1,
                totalItems: messagesData?.total || 0,
                itemsPerPage: 10,
                onPageChange: (newPage) => setPage(newPage),
                onItemsPerPageChange: () => {},
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* 消息详情对话框 */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>上行消息详情</DialogTitle>
            <DialogDescription>
              查看上行消息的详细信息
            </DialogDescription>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">消息ID</Label>
                  <p className="text-sm text-muted-foreground font-mono">{selectedMessage.messageId}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">手机号码</Label>
                  <p className="text-sm text-muted-foreground">{selectedMessage.mobile}</p>
                </div>
                
                <div className="col-span-2">
                  <Label className="text-sm font-medium">消息内容</Label>
                  <p className="text-sm text-muted-foreground bg-gray-50 p-2 rounded mt-1">{selectedMessage.content}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">运营商</Label>
                  <p className="text-sm text-muted-foreground">{selectedMessage.operator}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">位置</Label>
                  <p className="text-sm text-muted-foreground">{selectedMessage.province} {selectedMessage.city}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">状态</Label>
                  <p className="text-sm text-muted-foreground">{selectedMessage.status}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">通道ID</Label>
                  <p className="text-sm text-muted-foreground">{selectedMessage.channelId}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">接收时间</Label>
                  <p className="text-sm text-muted-foreground">{format(new Date(selectedMessage.createdAt), 'yyyy-MM-dd HH:mm:ss')}</p>
                </div>
                
                {selectedMessage.operatorSentAt && (
                  <div>
                    <Label className="text-sm font-medium">运营商发送时间</Label>
                    <p className="text-sm text-muted-foreground">{format(new Date(selectedMessage.operatorSentAt), 'yyyy-MM-dd HH:mm:ss')}</p>
                  </div>
                )}
                
                {selectedMessage.processedAt && (
                  <div>
                    <Label className="text-sm font-medium">处理时间</Label>
                    <p className="text-sm text-muted-foreground">{format(new Date(selectedMessage.processedAt), 'yyyy-MM-dd HH:mm:ss')}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-sm font-medium">推送状态</Label>
                  <p className="text-sm text-muted-foreground">{selectedMessage.pushStatus}</p>
                </div>
                
                {selectedMessage.pushRetryCount !== undefined && (
                  <div>
                    <Label className="text-sm font-medium">推送重试次数</Label>
                    <p className="text-sm text-muted-foreground">{selectedMessage.pushRetryCount} / {selectedMessage.pushMaxRetryCount || 0}</p>
                  </div>
                )}
                
                {selectedMessage.pushResponse && (
                  <div className="col-span-2">
                    <Label className="text-sm font-medium">推送响应</Label>
                    <p className="text-sm text-muted-foreground bg-gray-50 p-2 rounded mt-1">{selectedMessage.pushResponse}</p>
                  </div>
                )}
                
                {selectedMessage.errorCode && (
                  <div>
                    <Label className="text-sm font-medium">错误码</Label>
                    <p className="text-sm text-muted-foreground">{selectedMessage.errorCode}</p>
                  </div>
                )}
                
                {selectedMessage.errorMessage && (
                  <div className="col-span-2">
                    <Label className="text-sm font-medium">错误信息</Label>
                    <p className="text-sm text-muted-foreground">{selectedMessage.errorMessage}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-sm font-medium">Webhook状态</Label>
                  <p className="text-sm text-muted-foreground">{selectedMessage.webhookSent ? '已发送' : '未发送'}</p>
                </div>
                
                {selectedMessage.webhookSentAt && (
                  <div>
                    <Label className="text-sm font-medium">Webhook发送时间</Label>
                    <p className="text-sm text-muted-foreground">{format(new Date(selectedMessage.webhookSentAt), 'yyyy-MM-dd HH:mm:ss')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
